import { useCallback, useRef, useState } from "react";
import { Sort, useFeatureRequestStore } from "../../Stores/FeatureRequests";
import "./Roadmap.scss";
import { useRouterStore } from "../../Stores/Router";
import { useConfigStore } from "../../Stores/Config";
import { ReactComponent as PlusIcon } from "../../Assets/plus-solid.svg";
import { ReactComponent as ClockIcon } from "../../Assets/clock-solid.svg";
import { ReactComponent as StarIcon } from "../../Assets/star.svg";
import { generateText } from "@tiptap/react";
import ExtensionKit from "../../Components/tiptap/extenstions/extension-kit";
import { useSessionStore } from "../../Stores/Session";
import EmailUpvoteDialog from "../../Components/EmailUpvoteDialog/EmailUpvoteDialog";
import { activateOnKey } from "../../Helper/a11y";
import { FilterSelect } from "../../Components/FilterSelect/FilterSelect";

const sortOptions = [
    { value: "createdAt", key: "featureRequestSortDate" },
    { value: "upvotesCount", key: "featureRequestSort" },
];

// generateText builds the full tiptap schema — running it per card per render
// made every navigation into/out of the roadmap noticeably slower. The
// extension set is static, and the view remounts on each tab switch, so the
// derived text is cached against the (immutable, replaced-on-fetch)
// description object rather than per component instance.
let roadmapTextExtensions: any[] | null = null;
const descriptionTextCache = new WeakMap<object, string>();

const featureDescriptionText = (description: any): string | undefined => {
    if (!description) {
        return undefined;
    }
    if (typeof description !== "object") {
        return description;
    }

    const cached = descriptionTextCache.get(description);
    if (cached !== undefined) {
        return cached;
    }

    let text = "";
    try {
        if (!roadmapTextExtensions) {
            roadmapTextExtensions = [...ExtensionKit()];
        }
        text = generateText(description, roadmapTextExtensions);
    } catch (e) {
        text = "";
    }
    descriptionTextCache.set(description, text);
    return text;
};

function Roadmap() {
    const [pendingShareToken, setPendingShareToken] = useState<string | null>(null);
    const roadmapStore = useFeatureRequestStore();
    const session = useSessionStore((state) => state.session);
    const featureRequests = roadmapStore.featureRequests;
    const selectedColumn = roadmapStore.featureStatusFilter;
    const sort = roadmapStore.featureSort;
    const statusOptions = roadmapStore.statusOptions;
    const loadingRef = useRef(false);
    const roadmapRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            const nearBottom =
                target.scrollHeight - target.scrollTop - target.clientHeight < 200;
            if (nearBottom && roadmapStore.hasMoreFeatures && !loadingRef.current) {
                loadingRef.current = true;
                roadmapStore.fetchNextFeatureList();
                setTimeout(() => {
                    loadingRef.current = false;
                }, 500);
            }
        },
        [roadmapStore],
    );

    const handleUpvote = (shareToken: string) => {
        const email = session?.email;
        if (!email) {
            setPendingShareToken(shareToken);
            return;
        }
        roadmapStore.upvoteFeatureRequest(shareToken, email);
    };

    return (
        <div ref={roadmapRef} className="roadmap" onScroll={handleScroll}>
            <div className="roadmap-filters">
                <div className="roadmap-filter">
                    <FilterSelect
                        ariaLabel="Sort"
                        value={sort}
                        options={sortOptions.map((opt) => ({
                            value: opt.value,
                            label: useConfigStore.getState().translateText(opt.key),
                        }))}
                        onChange={(value) => {
                            roadmapStore.setFeatureSort(value as Sort);
                            if (roadmapRef.current) {
                                roadmapRef.current.scrollTo({ top: 0, behavior: "smooth" });
                            }
                        }}
                        leading={
                            sort === "upvotesCount" ? (
                                <StarIcon className="roadmap-filter-icon" />
                            ) : (
                                <ClockIcon className="roadmap-filter-icon" />
                            )
                        }
                    />
                </div>
                <div className="roadmap-filter">
                    <FilterSelect
                        ariaLabel="Filter by status"
                        value={selectedColumn}
                        options={statusOptions}
                        onChange={(value) => {
                            roadmapStore.setFeatureStatusFilter(value);
                            if (roadmapRef.current) {
                                roadmapRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                            }
                        }}
                        leading={
                            <span
                                className={`roadmap-filter-status-dot ${selectedColumn === "DONE" ? "roadmap-filter-status-dot--done" : ""
                                    }`}
                            />
                        }
                    />
                </div>
            </div>
            <div className="roadmap-list">
                {featureRequests.map((feature) => {
                    const upvotes = feature.upvotesCount ?? 0;
                    const description = featureDescriptionText(
                        feature?.formData?.description,
                    );

                    return (
                        <div
                            key={feature.id}
                            className="roadmap-card"
                        >
                            <div
                                className={`roadmap-card-upvotes ${feature.upvoted ? "roadmap-card-upvotes--upvoted" : ""}`}
                                role="button"
                                tabIndex={0}
                                aria-label="Upvote"
                                aria-pressed={!!feature.upvoted}
                                onClick={() => feature.shareToken && handleUpvote(feature.shareToken)}
                                onKeyDown={activateOnKey(() => feature.shareToken && handleUpvote(feature.shareToken))}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className=""
                                >
                                    <path d="m18 15-6-6-6 6" />
                                </svg>
                                <span>{upvotes > 99 ? "99+" : upvotes}</span>
                            </div>
                            <div
                                onClick={() =>
                                    useRouterStore.getState().pushPage("feature-request-details", {
                                        id: feature.shareToken,
                                    })
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={activateOnKey(() =>
                                    useRouterStore.getState().pushPage("feature-request-details", {
                                        id: feature.shareToken,
                                    })
                                )}
                                className="roadmap-card-content">
                                <div className="roadmap-card-title">{feature.title}</div>
                                {description && (
                                    <div className="roadmap-card-description">{description}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div className="action-overlay action-overlay--roadmap">
                    <div
                        className="action-button form-send-button"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            useRouterStore.getState().setPage("flow", {
                                flowId: "featurerequests",
                            });
                        }}
                        onKeyDown={activateOnKey(() => {
                            useRouterStore.getState().setPage("flow", {
                                flowId: "featurerequests",
                            });
                        })}
                    >
                        <PlusIcon />
                        {useConfigStore.getState().translateText("requestFeature")}
                    </div>
                </div>
            </div>
            <EmailUpvoteDialog
                open={!!pendingShareToken}
                onClose={() => setPendingShareToken(null)}
                onSubmit={(email) => {
                    if (pendingShareToken) {
                        roadmapStore.upvoteFeatureRequest(pendingShareToken, email);
                    }
                }}
            />
        </div>
    );
}

export default Roadmap;
